import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { FormErrorComponent } from '../form-error/form-error.component';

/**
 * IsMaskFieldComponent
 *
 * Campo com máscara de entrada (CPF, CNPJ, telefone, CEP, etc.).
 * Aplica a máscara visualmente e armazena o valor limpo (sem formatação).
 *
 * Padrão de máscara:
 * - 0 = dígito obrigatório
 * - 9 = dígito opcional
 * - A = letra obrigatória
 *
 * @example
 * <is-mask-field
 *   label="CPF"
 *   mask="000.000.000-00"
 *   formControlName="cpf"
 * ></is-mask-field>
 */
@Component({
  selector: 'is-mask-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormErrorComponent],
  templateUrl: './mask-field.component.html',
  styleUrls: ['./mask-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MaskFieldComponent),
      multi: true,
    },
  ],
})
export class MaskFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() mask = '';
  @Input() required = false;
  @Input() helpText = '';
  @Input() prefixIcon = '';
  @Input() control: AbstractControl | null = null;

  displayValue = '';
  isDisabled = false;

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  get computedPlaceholder(): string {
    return this.placeholder || this.mask.replace(/0/g, '_').replace(/9/g, '_').replace(/A/g, '_');
  }

  writeValue(val: string): void {
    this.displayValue = val ? this.applyMask(val) : '';
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  handleInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const clean = raw.replace(/\D/g, '');
    const masked = this.applyMask(clean);

    this.displayValue = masked;
    // Emite o valor limpo (sem máscara)
    this.onChange(clean);

    // Atualiza o input com o valor mascarado
    (event.target as HTMLInputElement).value = masked;
  }

  handleBlur(): void {
    this.onTouched();
  }

  private applyMask(value: string): string {
    if (!this.mask || !value) return value;

    let result = '';
    let valueIndex = 0;

    for (let i = 0; i < this.mask.length && valueIndex < value.length; i++) {
      const maskChar = this.mask[i];

      if (maskChar === '0' || maskChar === '9') {
        result += value[valueIndex];
        valueIndex++;
      } else if (maskChar === 'A') {
        result += value[valueIndex];
        valueIndex++;
      } else {
        result += maskChar;
      }
    }

    return result;
  }
}
